import { useSnackbar } from 'notistack';
import { useMutation } from 'react-query';
import { useService } from 'shared/providers';
import { QuoteDocument } from 'shared/types/quotes';
import { formatDateTimeEST } from 'shared/utility';
import { usePdfExport } from './quotes';

interface IExportQuoteToPdfMutationProps {
  quoteId: string;
  dateSubmitted: string;
}
export function useExportQuoteToPdfMutation({ quoteId, dateSubmitted }: IExportQuoteToPdfMutationProps) {
  const { quoteService } = useService();
  const { enqueueSnackbar } = useSnackbar();
  const pdfMutation = usePdfExport<QuoteDocument>();

  return useMutation<QuoteDocument, Error>(() => quoteService.getQuote(quoteId), {
    onSuccess: async data => {
      if (!data) {
        enqueueSnackbar(`No quote was found to export.`, {
          variant: 'warning',
        });
        return;
      }

      const products = data.products.map(p => ({
        ...p,
        isProductAddedAfterQuoteSubmit: p.createdDate && new Date(p.createdDate) > new Date(dateSubmitted),
      }));

      await pdfMutation.mutateAsync({
        exportId: 'quote',
        data: {
          ...data,
          dateSubmitted: data.dateSubmitted ? formatDateTimeEST(new Date(data.dateSubmitted)) : '',
          dateRevised: data.dateRevised ? formatDateTimeEST(new Date(data.dateRevised)) : '',
          dateApproved: data.dateApproved ? formatDateTimeEST(new Date(data.dateApproved)) : '',
          createdOn: data.createdOn ? formatDateTimeEST(new Date(data.createdOn)) : '',
          lastModifiedOn: data.lastModifiedOn ? formatDateTimeEST(new Date(data.lastModifiedOn)) : '',
          products,
        },
        fileName: `Quote Request${!!data?.quoteNumber ? ' ' + data?.quoteNumber : ''}`,
      });
    },
    onError: () => {
      enqueueSnackbar('There was a problem exporting the quote', { variant: 'error' });
    },
  });
}
